/**
 * Stock Images API Client
 */

import { apiGet, apiPost, apiPut } from './client';

// ============================================
// Types
// ============================================

export type ImageSource = 'pexels' | 'unsplash' | 'customer_upload';
export type ImageType = 'hero' | 'about' | 'menu_item' | 'service' | 'gallery' | 'team' | 'interior' | 'exterior';
export type ReviewStatus = 'pending' | 'approved' | 'rejected' | 'flagged';
export type VisionConfidence = 'high' | 'medium' | 'low' | 'none';

export interface CachedImage {
  id: string;
  source: ImageSource;
  source_id: string | null;
  source_url: string | null;
  photographer: string | null;
  r2_key: string;
  r2_url: string;
  industry: string;
  sub_category: string | null;
  image_type: ImageType;
  tags: string[];
  vision_confidence: VisionConfidence | null;
  vision_reasoning: string | null;
  original_query: string | null;
  review_status: ReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  times_used: number;
  last_used_at: string | null;
  width: number | null;
  height: number | null;
  created_at: string;
  updated_at: string;
}

export interface ImageListResponse {
  success: boolean;
  images: CachedImage[];
  total: number;
  limit: number;
  offset: number;
}

export interface ImageStatsResponse {
  success: boolean;
  stats: {
    pending: number;
    approved: number;
    rejected: number;
    flagged: number;
    total: number;
  };
}

export interface ImageFilters {
  industry?: string;
  subCategory?: string;
  imageType?: ImageType;
  reviewStatus?: ReviewStatus;
  limit?: number;
  offset?: number;
}

export interface WebsiteImageSlot {
  slot: string;
  image: CachedImage | null;
}

export interface FetchImageRequest {
  industry: string;
  subCategory?: string;
  imageType: ImageType;
  intent: string;
  context: string;
}

// ============================================
// API Functions
// ============================================

/**
 * List images with optional filters
 */
export async function getImages(filters: ImageFilters = {}): Promise<ImageListResponse> {
  const params = new URLSearchParams();

  if (filters.industry) params.set('industry', filters.industry);
  if (filters.subCategory) params.set('subCategory', filters.subCategory);
  if (filters.imageType) params.set('imageType', filters.imageType);
  if (filters.reviewStatus) params.set('reviewStatus', filters.reviewStatus);
  if (filters.limit) params.set('limit', filters.limit.toString());
  if (filters.offset) params.set('offset', filters.offset.toString());

  const queryString = params.toString();
  const endpoint = `/api/admin/images${queryString ? `?${queryString}` : ''}`;

  return apiGet<ImageListResponse>(endpoint);
}

/**
 * Get image statistics by review status
 */
export async function getImageStats(): Promise<ImageStatsResponse> {
  return apiGet<ImageStatsResponse>('/api/admin/images/stats');
}

/**
 * Get images pending review
 */
export async function getReviewQueue(limit = 50, offset = 0): Promise<ImageListResponse> {
  return apiGet<ImageListResponse>(`/api/admin/images/review-queue?limit=${limit}&offset=${offset}`);
}

/**
 * Get single image by ID
 */
export async function getImage(imageId: string): Promise<{ success: boolean; image: CachedImage }> {
  return apiGet<{ success: boolean; image: CachedImage }>(`/api/admin/images/${imageId}`);
}

/**
 * Update image metadata/status
 */
export async function updateImage(
  imageId: string,
  updates: {
    industry?: string;
    sub_category?: string | null;
    image_type?: ImageType;
    tags?: string[];
    review_status?: ReviewStatus;
    reviewed_by?: string;
    review_notes?: string;
  }
): Promise<{ success: boolean; image: CachedImage }> {
  return apiPut<{ success: boolean; image: CachedImage }>(`/api/admin/images/${imageId}`, updates);
}

/**
 * Bulk update review status for multiple images
 */
export async function bulkUpdateImages(
  imageIds: string[],
  status: ReviewStatus,
  reviewedBy: string
): Promise<{ success: boolean; updated: number }> {
  return apiPost<{ success: boolean; updated: number }>('/api/admin/images/bulk', {
    imageIds,
    status,
    reviewedBy,
  });
}

/**
 * Get images used by a specific website
 */
export async function getWebsiteImages(websiteId: string): Promise<{
  success: boolean;
  websiteId: string;
  images: WebsiteImageSlot[];
}> {
  return apiGet<{
    success: boolean;
    websiteId: string;
    images: WebsiteImageSlot[];
  }>(`/api/admin/images/website/${websiteId}`);
}

/**
 * Assign an image to a website slot
 */
export async function assignWebsiteImage(
  websiteId: string,
  imageId: string,
  imageSlot: string
): Promise<{ success: boolean; message: string }> {
  return apiPost<{ success: boolean; message: string }>(`/api/admin/images/website/${websiteId}`, {
    imageId,
    imageSlot,
  });
}

/**
 * Fetch a new stock image with Vision validation
 */
export async function fetchStockImage(request: FetchImageRequest): Promise<{
  success: boolean;
  image: CachedImage | null;
  fallback: boolean;
  attempts: number;
  error?: string;
}> {
  return apiPost<{
    success: boolean;
    image: CachedImage | null;
    fallback: boolean;
    attempts: number;
    error?: string;
  }>('/api/admin/images/fetch', request);
}

// ============================================
// Helper Functions
// ============================================

export const INDUSTRIES = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'professional-services', label: 'Professional Services' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'healthcare', label: 'Healthcare' },
];

export const IMAGE_TYPES: { value: ImageType; label: string }[] = [
  { value: 'hero', label: 'Hero' },
  { value: 'about', label: 'About' },
  { value: 'service', label: 'Service' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'team', label: 'Team' },
  { value: 'interior', label: 'Interior' },
  { value: 'exterior', label: 'Exterior' },
  { value: 'menu_item', label: 'Menu Item' },
];

export const REVIEW_STATUSES: { value: ReviewStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'flagged', label: 'Flagged' },
];

export function getStatusColor(status: ReviewStatus): string {
  switch (status) {
    case 'approved':
      return 'text-green-600 bg-green-100';
    case 'rejected':
      return 'text-red-600 bg-red-100';
    case 'flagged':
      return 'text-yellow-600 bg-yellow-100';
    case 'pending':
    default:
      return 'text-blue-600 bg-blue-100';
  }
}

export function getConfidenceColor(confidence: VisionConfidence | null): string {
  switch (confidence) {
    case 'high':
      return 'text-green-600';
    case 'medium':
      return 'text-yellow-600';
    case 'low':
      return 'text-orange-600';
    case 'none':
    default:
      return 'text-gray-400';
  }
}
